import { useState, useRef, useEffect } from "react";
import Visualizer from "./Visualizer";
import Waveform from "./Waveform";
import Mp3CommentReader from "./Mp3CommentReader";

import imageClassicHouse from './assets/Classic House.jpg';
import imageTechno from './assets/Techno.jpg';
import imageDeepHouse from './assets/Deep House.jpg';
import imageHouse from './assets/House.jpg';
import imageHardHouse from './assets/Hard House.jpg';
import imageTechHouse from './assets/Tech House.jpg';
import imageDrumAndBass from './assets/Drum and Bass.jpg';
import imageHardcore from './assets/Hardcore.jpg';

const MusicPlayerTable = ({ tracks, initialTrack }) => {
    // filtering, searching and sorting state
    const [filteredTracks, setFilteredTracks] = useState(tracks);
    const [sortColumn, setSortColumn] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc");
    const [filterGenre, setFilterGenre] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [showTrackList, setShowTrackList] = useState(false);
    const [showSortPanel, setShowSortPanel] = useState(false);
    const sortPanelRef = useRef(null);
    const sortButtonRef = useRef(null);

    const sort = (column) => {
        const order = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
        setSortColumn(column);
        setSortOrder(order);
    };

    const resetSort = () => {
        setSortColumn(null);
        setSortOrder("asc");
    };

    const toggleSortPanel = () => {
        setShowSortPanel(!showSortPanel);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            // only close if clicking outside both the panel and the button
            if (sortPanelRef.current && !sortPanelRef.current.contains(event.target)
                && sortButtonRef.current && !sortButtonRef.current.contains(event.target)) {
                setShowSortPanel(false);
            }
        }

        if (showSortPanel) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSortPanel]);

    useEffect(() => {
        const sortedTracks = [...tracks].sort((a, b) => {
            if (sortColumn === "shuffle") return Math.random() - 0.5; // random order
            if (a[sortColumn] < b[sortColumn]) return sortOrder === "asc" ? -1 : 1;
            if (a[sortColumn] > b[sortColumn]) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });

        const filteredTracks = sortedTracks.filter(track => 
            (filterGenre ? track.genre === filterGenre : true) &&
            (searchQuery ? track.name.toLowerCase().includes(searchQuery.toLowerCase()) : true));
        setFilteredTracks(filteredTracks);
    }, [sortColumn, sortOrder, filterGenre, searchQuery, tracks]);

    // audio context for visualization component
    const [audioContext, setAudioContext] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTrack, setCurrentTrack] = useState(null);
    const audioRef = useRef(null);

    if (initialTrack && currentTrack === null) {
        const track = tracks.find(t => t.name === initialTrack);
        if (track) {
            setTimeout(() => {
                play(track)
            }, 0);
        }
    }

    const play = (track) => {
        setCurrentTrack(track);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current.src = "./" + track.src;
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    const playNextTrack = () => {
        // if find our track in the current list, play the next track
        // we default back to the first track in the current list if not found
        let nextTrackIndex = 0;
        const currentTrackIndex = filteredTracks.findIndex(track => track === currentTrack);
        if (currentTrackIndex !== -1) {
            if (filteredTracks.length > currentTrackIndex+1) {
                nextTrackIndex = currentTrackIndex+1;
            }
            else return;    // don't loop the list
        }
        if (filteredTracks.length) play(filteredTracks[nextTrackIndex]);
    };

    const cssfilter = window.navigator.userAgent.indexOf("Firefox") !== -1 ? "none" : "invert(93%)";
    
    useEffect(() => {
        let audioCtx = null;
        let dataArray = null;
        let analyser = null;
        let bufferLength = 0;
        let source = null;

        const audio = audioRef.current;

        audio.onplay = () => {
            const firstRun = !audioCtx;
            if (firstRun) {
                // all this should be done once - but only when everything is available in the DOM 
                console.debug("Creating new AudioContext");
                audioCtx = new window.AudioContext();
                source = audioCtx.createMediaElementSource(audio);
                analyser = audioCtx.createAnalyser();
                source.connect(analyser);
                analyser.connect(audioCtx.destination);
                analyser.fftSize = 512;
                bufferLength = analyser.frequencyBinCount - 64; // final ~10% is just zeroes
                dataArray = new Uint8Array(bufferLength);
            }
            audioCtx.resume().then(() => {
                console.debug("AudioContext resumed");
                if (firstRun) setAudioContext({ analyser, dataArray, bufferLength });
            });
        };
    }, []);

    const getGenreImage = (track) => {
        switch (track.genre) {
            case "Classic House": return imageClassicHouse;
            case "Techno": return imageTechno;
            case "Deep House": return imageDeepHouse;
            case "House": case "Funky House": case "House Party": case "Garage": return imageHouse;
            case "Hard House": return imageHardHouse;
            case "Tech House": return imageTechHouse;
            case "Drum & Bass": return imageDrumAndBass;
            case "Hardcore": case "Jungle": return imageHardcore;
            default: return "";
        }
    };

    return (
    <div>
        <div className="centerflex">
            <div className="box center">
                <h3>Playing: {currentTrack ? currentTrack.name : "Select a mix!"}&nbsp;{currentTrack ? <button className="small-button" onClick={()=> setShowTrackList(!showTrackList)} title="track list">☷</button> : null}</h3>
                <audio style={{filter:cssfilter}} ref={audioRef} controls onPause={() => setIsPlaying(false)} onPlay={() => setIsPlaying(true)} onEnded={() => playNextTrack()} />
                <Waveform audioContext={audioContext} />
                {showTrackList && currentTrack ? (
                    <Mp3CommentReader url={currentTrack.src} />
                ) : null}
            </div>
        </div>

        <div className="main-content">
            <div>
                <div className="center filters">
                    <select onChange={(e) => setFilterGenre(e.target.value)}>
                        <option value="">Genre (All)</option>
                        {Array.from(new Set(tracks.map(track => track.genre))).map((genre, index) => (
                        <option key={index} value={genre}>{genre}</option>
                        ))}
                    </select>

                    <input
                        type="text" 
                        placeholder="Search..." 
                        onChange={(e) => setSearchQuery(e.target.value)} 
                    />

                    <button ref={sortButtonRef} className="toggle-sort" title="sort options" onClick={toggleSortPanel}>⮃</button>

                    <div ref={sortPanelRef} className={`sort-panel ${showSortPanel ? 'visible' : 'hidden'}`}>
                        <h3>Sort Options</h3>
                        <span onClick={() => sort("name")}>Name {sortColumn === "name" ? (sortOrder === "asc" ? "▲" : "▼") : ""}</span>
                        <span onClick={() => sort("length")}>Length {sortColumn === "length" ? (sortOrder === "asc" ? "▲" : "▼") : ""}</span>
                        <span onClick={() => sort("genre")}>Genre {sortColumn === "genre" ? (sortOrder === "asc" ? "▲" : "▼") : ""}</span>
                        <span onClick={() => sort("shuffle")}>Shuffle</span>
                        <span onClick={() => resetSort()}>Reset Sort</span>
                    </div>
                </div>

                <table>
                    <tbody>
                    {filteredTracks.map((track, index) => (
                    <tr key={index} onClick={() => play(track)} className={track === currentTrack ? 'current' : ''}>
                        <td className={`genre ${track === currentTrack ? 'current' : ''}`}>
                            <img src={getGenreImage(track)} width="40" height="40" title={track.genre} alt="" />
                        </td>
                        <td className="track-name">{track.name}</td>
                        <td>{track.length.substring(0,2)} {track.length.substring(2)}</td>
                    </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <Visualizer audioContext={audioContext} isPlaying={isPlaying} />
        </div>
    </div>
    );
};

export default MusicPlayerTable;